import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type {
    UerRemoveActKc2PriceFromWorkOrderMutation,
    UerRemovePriceFromWorkOrderMutation,
    WorkOrderPriceData,
} from 'gql/graphql';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import type { IRowNode } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { qglRemoveActKC2PriceFromWorkOrder, qglRemovePriceFromWorkOrder } from '../workOrderQueries';

export const WorkOrderPriceDeleteOneItem = ({ gridTableRef }: { gridTableRef: RefObject<AgGridReact> }) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { rowId } = useParams();
    const { id: projectId, orderId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const workOrderStatus = useMemo(
        () =>
            projectData?.contractNavigation?.data?.workOrderNavs?.find(
                (workOrder: any) => workOrder.id === Number(orderId),
            )?.status,
        [projectData?.contractNavigation?.data?.workOrderNavs, orderId],
    );

    const isInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;

    const query: any = useMemo(
        () => (isInProgress ? qglRemoveActKC2PriceFromWorkOrder : qglRemovePriceFromWorkOrder),
        [isInProgress],
    );

    const [deleteData, deleteStatus, deleteError, deleteActionCreator] = useRequest<
        UerRemoveActKc2PriceFromWorkOrderMutation & UerRemovePriceFromWorkOrderMutation
    >({
        requestId: `removeWorkOrderPriceRow_${rowId}`,
        query,
        withEffect: false,
    });

    const currentRowData = useMemo<WorkOrderPriceData | null>(() => {
        let rowData: WorkOrderPriceData | null = null;

        gridTableRef?.current?.api?.forEachNode((rowNode: IRowNode) => {
            if (rowNode.data && rowNode.data.id === Number(rowId)) {
                rowData = rowNode.data;
            }
        });

        return rowData;
    }, [rowId, gridTableRef]);

    const handleDeleteExactly = useCallback(() => {
        const variables = { workOrderPriceIds: [Number(rowId)] };

        dispatch(deleteActionCreator(variables));

        setIsSubmitted(true);
    }, [deleteActionCreator, dispatch, rowId]);

    const handlerCloseModal = useCallback(
        (wasSubmit) => {
            if (!wasSubmit) {
                navigate('../', { replace: true });
            }
        },
        [navigate],
    );

    useEffect(() => {
        const isResult = isInProgress
            ? deleteData?.uerRemoveActKC2PriceFromWorkOrder?.result
            : deleteData?.uerRemovePriceFromWorkOrder?.result;

        if (deleteStatus === RequestStatus.SUCCESS && isResult) {
            navigate('../', { replace: true, state: { reload: rowId } });
        }
    }, [
        navigate,
        deleteStatus,
        rowId,
        isInProgress,
        deleteData?.uerRemoveActKC2PriceFromWorkOrder?.result,
        deleteData?.uerRemovePriceFromWorkOrder?.result,
    ]);

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
        handlerCloseModal(false);
    }, [handlerCloseModal]);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(
            deleteData?.uerRemovePriceFromWorkOrder?.validation ||
                deleteData?.uerRemoveActKC2PriceFromWorkOrder?.validation,
        );
    }, [
        deleteData?.uerRemovePriceFromWorkOrder?.validation,
        deleteData?.uerRemoveActKC2PriceFromWorkOrder?.validation,
    ]);

    const textConfirmation = useMemo(() => {
        if (currentRowData?.type === WorkOrderPriceTypeEnum.Uer) {
            return 'Вы уверены, что хотите удалить расценку? Все дочерние элементы (коэффициенты, МТР) и объемы по расценке будут удалены';
        }

        if (
            currentRowData?.type === WorkOrderPriceTypeEnum.Coefficient ||
            currentRowData?.type === WorkOrderPriceTypeEnum.TechPartCoefficient ||
            currentRowData?.type === WorkOrderPriceTypeEnum.ConstraintCoefficient ||
            currentRowData?.type === WorkOrderPriceTypeEnum.DismantlingCoefficient
        ) {
            return 'Вы уверены, что хотите удалить коэффициент к расценке?';
        }

        if (
            currentRowData?.type === WorkOrderPriceTypeEnum.Mtr ||
            currentRowData?.type === WorkOrderPriceTypeEnum.MtrContractorEquipment ||
            currentRowData?.type === WorkOrderPriceTypeEnum.MtrContractorMaterial ||
            currentRowData?.type === WorkOrderPriceTypeEnum.MtrCustomerEquipment
        ) {
            return 'Вы уверены, что хотите удалить МТР?';
        }

        return 'Вы уверены, что хотите удалить запись?';
    }, [currentRowData]);

    return (
        <>
            <ConfirmationDialog
                textHeader="УДАЛЕНИЕ ЗАПИСИ"
                textConfirmation={textConfirmation}
                isOpened
                onSubmit={handleDeleteExactly}
                onClose={handlerCloseModal}
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
            />
            <ErrorHandlingModal
                titleText="Удаление записи"
                isSubmitted={isSubmitted}
                statusRequest={deleteStatus}
                dataRequest={
                    isInProgress
                        ? deleteData?.uerRemoveActKC2PriceFromWorkOrder
                        : deleteData?.uerRemovePriceFromWorkOrder
                }
                errorsRequest={deleteError}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
        </>
    );
};

WorkOrderPriceDeleteOneItem.displayName = 'WorkOrderPriceDeleteOneItem';
